import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  getSettlementReview,
  getVenueAdminSettlementOverview,
  markSettlementSettled,
} from "../api";

import FormModal from "../../../components/form/FormModal";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormTextarea from "../../../components/form/FormTextarea";

import "../components/SettlementsDashboard.css";

const fmt = new Intl.NumberFormat("en-IN");
const money = (value) => `₹${fmt.format(Number(value || 0))}`;

const formatDate = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const getAdminBasePath = (pathname = "") => {
  if (pathname.startsWith("/venue-admin")) return "/venue-admin";
  if (pathname.startsWith("/country-admin")) return "/country-admin";
  return "/super-admin";
};

const csvEscape = (value) => {
  if (value === null || value === undefined) return '""';

  let text = String(value).replace(/\r?\n|\r/g, " ").trim();

  // Prevent spreadsheet formula injection.
  if (/^[=+\-@]/.test(text)) {
    text = `'${text}`;
  }

  return `"${text.replace(/"/g, '""')}"`;
};

const downloadCsv = (filename, rows) => {
  const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");

  const blob = new Blob([`\uFEFF${csv}\n`], {
    type: "text/csv;charset=utf-8;",
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();

  link.remove();
  window.URL.revokeObjectURL(url);
};

const SettlementReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const basePath = useMemo(() => getAdminBasePath(pathname), [pathname]);
  const isVenueAdmin = pathname.startsWith("/venue-admin");

  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState(null);

  const [approvalOpen, setApprovalOpen] = useState(false);
  const [utrNumber, setUtrNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const settlement = review?.settlement || {};
  const summary = review?.summary || {};
  const bookings = review?.bookingsIncluded || [];

  const canMarkAsSettled = settlement?.canMarkAsSettled === true;

  const loadReview = useCallback(async () => {
    if (!id) return;

    setLoading(true);

    try {
      if (isVenueAdmin) {
        const data = await getVenueAdminSettlementOverview();
        const items = [
          ...(data?.pendingSettlements || []),
          ...(data?.paidSettlements || []),
        ];

        const item = items.find(
          (settlementItem) => String(settlementItem?._id) === String(id)
        );

        if (!item) {
          setReview(null);
          return;
        }

        setReview({
          settlement: {
            ...item,
            canMarkAsSettled: false,
          },
          summary: {
            collectedFromPlayers: item?.totalCollected || 0,
            ongroundFee: item?.ongroundFee || item?.totalCommission || 0,
            venueReceives:
              item?.venueReceives ||
              item?.venueSettlement ||
              item?.amountDue ||
              item?.paidAmount ||
              0,
            bookingsCount:
              item?.bookingsCount ||
              item?.unpaidBookings ||
              item?.bookingsIncluded?.length ||
              0,
          },
          bookingsIncluded: item?.bookingsIncluded || [],
        });

        return;
      }

      const data = await getSettlementReview(id);
      setReview(data || null);
    } catch (error) {
      console.error("getSettlementReview error:", error);
      toast.error(
        error?.response?.data?.message || "Failed to load settlement review"
      );
      setReview(null);
    } finally {
      setLoading(false);
    }
  }, [id, isVenueAdmin]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  const handleBack = () => {
    navigate(`${basePath}/settlements`);
  };

  const handleOpenApproval = () => {
    if (!canMarkAsSettled) {
      toast.info("Only READY_FOR_SETTLEMENT records can be approved.");
      return;
    }

    setUtrNumber("");
    setNotes("");
    setErrors({});
    setApprovalOpen(true);
  };

  const handleCloseApproval = () => {
    if (submitting) return;

    setApprovalOpen(false);
    setUtrNumber("");
    setNotes("");
    setErrors({});
  };

  const validateApproval = () => {
    const nextErrors = {};

    if (!String(utrNumber || "").trim()) {
      nextErrors.utrNumber = "UTR number is required";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      toast.warn("Please enter UTR number");
    }

    return nextErrors;
  };

  const handleConfirmSettlement = async (event) => {
    event.preventDefault();

    if (submitting) return;

    const validationErrors = validateApproval();
    if (Object.keys(validationErrors).length > 0) return;

    const cleanUtr = String(utrNumber || "").trim();

    setSubmitting(true);

    try {
      await markSettlementSettled(id, {
        utrNumber: cleanUtr,
        notes,
      });

      toast.success("Settlement marked as settled");
      setApprovalOpen(false);
      navigate(`${basePath}/settlements`);
    } catch (error) {
      console.error("markSettlementSettled error:", error);
      toast.error(
        error?.response?.data?.message || "Failed to approve settlement"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportReview = () => {
    if (!review) {
      toast.warn("Settlement data is not loaded yet");
      return;
    }

    const venueName = settlement?.venue?.name || "Venue";
    const cycle = settlement?.cycleLabel || `Settlement-${id}`;

    const rows = [
      [
        "Settlement",
        "Venue",
        "Period Start",
        "Period End",
        "Status",
        "Booking ID",
        "User",
        "Ground",
        "Booking Date",
        "Slot",
        "Collected",
        "Onground Fee",
        "Gateway Fee Borne By Onground",
        "Onground Net Earning",
        "Venue Receives",
        "Payment Gateway",
        "Payment Verified At",
      ],
      ...bookings.map((booking) => [
        cycle,
        venueName,
        formatDate(settlement.periodStart),
        formatDate(settlement.periodEnd),
        settlement.status || "",
        booking.bookingId || "",
        booking.userName || "",
        booking.groundName || "",
        formatDate(booking.bookingDate),
        booking.timeSlot?.start && booking.timeSlot?.end
          ? `${booking.timeSlot.start} - ${booking.timeSlot.end}`
          : "",
        booking.collectedAmount || 0,
        booking.ongroundFee || 0,
        booking.gatewayFeeBorneByOnground || 0,
        booking.ongroundNetEarning || 0,
        booking.venueReceives || 0,
        booking.paymentGateway || "",
        formatDateTime(booking.paymentVerifiedAt),
      ]),
    ];

    const safeCycle = String(cycle || "settlement")
      .replace(/[^a-z0-9-_]+/gi, "-")
      .toLowerCase();

    downloadCsv(`${safeCycle}-review.csv`, rows);
    toast.success("Review CSV exported");
  };

  if (loading) {
    return (
      <div className="setd setd-review-page">
        <div className="setd-loading">Loading settlement review…</div>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="setd setd-review-page">
        <div className="setd-breadcrumb">
          <button type="button" onClick={handleBack}>
            Settlements
          </button>
          <span>›</span>
          <span>{isVenueAdmin ? "Settlement Details" : "Review & Settle"}</span>
        </div>

        <div className="setd-empty">Settlement details not found.</div>
      </div>
    );
  }

  return (
    <div className="setd setd-review-page">
      <div className="setd-breadcrumb">
        <button type="button" onClick={handleBack}>
          Settlements
        </button>
        <span>›</span>
        <span>{isVenueAdmin ? "Settlement Details" : "Review & Settle"}</span>
      </div>

      <div className="setd-review-page-head">
        <div>
          <h1 className="setd-title">
            {isVenueAdmin ? "Settlement Details" : "Review & Settle Settlement"}
          </h1>
          <p className="setd-subtitle">
            {isVenueAdmin
              ? "View settlement details and included bookings"
              : "Review settlement details and approve to initiate payout"}
          </p>
        </div>

        {!isVenueAdmin && (
          <div className="setd-review-page-actions">
            <button
              type="button"
              className="setd-btn setd-btn-secondary"
              onClick={handleExportReview}
            >
              Export
            </button>

            <button
              type="button"
              className="setd-btn setd-btn-primary"
              onClick={handleOpenApproval}
              disabled={!canMarkAsSettled}
            >
              Approve &amp; Settle
            </button>
          </div>
        )}
      </div>

      <section className="setd-review-hero">
        <div className="setd-review-hero-left">
          <div className="setd-review-cycle-row">
            <h2>{settlement.cycleLabel || "Settlement"}</h2>

            <span className="setd-status-pill">
              {settlement.status || "—"}
            </span>
          </div>

          <div className="setd-review-period-row">
            <div className="setd-review-calendar">▣</div>

            <div>
              <strong>
                {formatDate(settlement.periodStart)} –{" "}
                {formatDate(settlement.periodEnd)}
              </strong>
              <p>Settlement Period</p>
            </div>
          </div>
        </div>

        <div className="setd-review-venue-receives">
          <span>Venue Receives</span>
          <strong>{money(summary.venueReceives)}</strong>
        </div>
      </section>

      <section className="setd-review-kpis">
        <div className="setd-review-kpi">
          <span>Collected From Players</span>
          <strong>{money(summary.collectedFromPlayers)}</strong>
          <p>Total amount collected from players</p>
        </div>

        <div className="setd-review-kpi">
          <span>Onground Fee</span>
          <strong>{money(summary.ongroundFee)}</strong>
          <p>Platform fee collected by Onground</p>
        </div>

        {!isVenueAdmin && (
          <>
            <div className="setd-review-kpi">
              <span>Gateway Fee Borne By Onground</span>
              <strong>{money(summary.gatewayFeeBorneByOnground)}</strong>
              <p>Non-recoverable gateway charges</p>
            </div>

            <div className="setd-review-kpi">
              <span>Onground Net Earning</span>
              <strong>{money(summary.ongroundNetEarning)}</strong>
              <p>Total earning after gateway fee</p>
            </div>
          </>
        )}
      </section>

      <section className="setd-review-table-card">
        <div className="setd-bookings-head">
          <h4>Bookings Included</h4>
          <span>{summary.bookingsCount || bookings.length} booking(s)</span>
        </div>

        <div className="setd-bookings-wrap">
          <table className="setd-bookings-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>User</th>
                <th>Ground</th>
                <th>Date / Slot</th>
                <th className="setd-money-col">Collected</th>
                <th className="setd-money-col">Onground Fee</th>
                <th className="setd-money-col">Venue Receives</th>
              </tr>
            </thead>

            <tbody>
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="setd-empty-cell">
                    No bookings found.
                  </td>
                </tr>
              ) : (
                bookings.map((booking) => (
                  <tr key={booking.paymentId}>
                    <td>{booking.bookingId || "—"}</td>
                    <td>{booking.userName || "—"}</td>
                    <td>
                      <div>{booking.groundName || "—"}</div>
                      <small>{booking.groundType || ""}</small>
                    </td>
                    <td>
                      <div>{formatDate(booking.bookingDate)}</div>
                      <small>
                        {booking.timeSlot?.start && booking.timeSlot?.end
                          ? `${booking.timeSlot.start} – ${booking.timeSlot.end}`
                          : "—"}
                      </small>
                    </td>
                    <td className="setd-money-col">
                      {money(booking.collectedAmount)}
                    </td>
                    <td className="setd-money-col">
                      {money(booking.ongroundFee)}
                    </td>
                    <td className="setd-money-col">
                      {money(booking.venueReceives)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {!isVenueAdmin && (
        <section className="setd-review-warning">
          <span>⚠</span>
          <p>
            Please review all details carefully before approving. Once approved,
            the payout will be recorded as settled and cannot be reversed from
            this screen.
          </p>
        </section>
      )}

      {!isVenueAdmin && (
        <FormModal
          open={approvalOpen}
          title="Approve Settlement"
          width="560px"
          submitting={submitting}
          submitText="Confirm Approval"
          submittingText="Approving..."
          cancelText="Cancel"
          onClose={handleCloseApproval}
          onSubmit={handleConfirmSettlement}
        >
          <div className="setd-approval-modal">
            <div className="setd-settle-amount">
              <span>Venue Receives</span>
              <strong>{money(summary.venueReceives)}</strong>
            </div>

            <FormField
              label="UTR Number"
              error={errors.utrNumber}
              hint="UTR number is mandatory before approving the settlement."
            >
              <FormInput
                type="text"
                value={utrNumber}
                onChange={(event) => {
                  setUtrNumber(event.target.value);
                  if (errors.utrNumber) {
                    setErrors((current) => {
                      const next = { ...current };
                      delete next.utrNumber;
                      return next;
                    });
                  }
                }}
                placeholder="Enter UTR number"
                disabled={submitting}
                required
              />
            </FormField>

            <FormField label="Notes" hint="Optional internal note for this payout.">
              <FormTextarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Optional notes"
                rows={3}
                disabled={submitting}
              />
            </FormField>
          </div>
        </FormModal>
      )}
    </div>
  );
};

export default SettlementReviewPage;